// Mobile Gallery Carousel
// Handles the horizontal product gallery below the desktop breakpoint
class MobileGalleryCarousel {
  constructor() {
    this.gallery = document.querySelector('.product-gallery');
    this.container = document.querySelector('.product-gallery__container');
    this.items = document.querySelectorAll('.product-gallery__item');
    
    if (!this.gallery || !this.container || this.items.length === 0) {
      return;
    }

    if (window.innerWidth >= 1025) {
      return;
    }
    
    this.useTouchCarousel =
      navigator.maxTouchPoints > 0 &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    this.gallery.classList.toggle('product-gallery--touch-carousel', this.useTouchCarousel);
    this.gallery.classList.toggle('product-gallery--pointer-carousel', !this.useTouchCarousel);
    
    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.translateX = 0;
    this.animationId = null;
    this.isHorizontalSwipe = false;
    this.slidePreloads = new Map();
    this.navigationRequest = 0;
    
    this.init();
  }
  
  init() {
    this.previousButton = this.gallery.querySelector('.product-gallery__button--prev');
    this.nextButton = this.gallery.querySelector('.product-gallery__button--next');
    this.previousButton?.addEventListener('click', () => this.goToSlide(this.currentIndex - 1));
    this.nextButton?.addEventListener('click', () => this.goToSlide(this.currentIndex + 1));
    this.updateButtons();
    this.preloadAdjacentSlides();
    
    // Set initial position
    this.updatePosition();
    
    if (this.useTouchCarousel) {
      this.container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.container.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
      this.container.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    }
    
    // Handle window resize
    window.addEventListener('resize', this.handleResize.bind(this));
    
    // Prevent image drag on mobile
    this.container.addEventListener('dragstart', (e) => e.preventDefault());
  }
  
  updatePosition(immediate = false) {
    if (immediate) {
      this.container.style.transition = 'none';
    } else {
      this.container.style.transition = 'transform 0.3s ease-out';
    }
    
    const translate = -this.currentIndex * 100;
    this.container.style.transform = `translateX(${translate}%)`;
  }
  
  async goToSlide(index) {
    if (index < 0 || index >= this.items.length) return;

    const request = ++this.navigationRequest;
    await this.preloadSlide(index);
    if (request !== this.navigationRequest) return;
    
    this.currentIndex = index;
    this.preloadAdjacentSlides();
    this.updatePosition();
    this.updateButtons();
  }

  preloadSlide(index) {
    if (index < 0 || index >= this.items.length) return Promise.resolve();
    if (this.slidePreloads.has(index)) return this.slidePreloads.get(index);

    const image = this.items[index].querySelector('img');
    if (!image) return Promise.resolve();

    image.loading = 'eager';

    const preload = new Promise((resolve) => {
      const decode = () => {
        if (typeof image.decode === 'function') {
          image.decode().catch(() => {}).finally(resolve);
        } else {
          resolve();
        }
      };

      if (image.complete) {
        decode();
      } else {
        image.addEventListener('load', decode, { once: true });
        image.addEventListener('error', resolve, { once: true });
      }
    });

    this.slidePreloads.set(index, preload);
    return preload;
  }

  preloadAdjacentSlides() {
    [this.currentIndex - 1, this.currentIndex, this.currentIndex + 1].forEach((index) => {
      this.preloadSlide(index);
    });
  }

  isSlideReady(index) {
    const image = this.items[index]?.querySelector('img');
    return !image || (image.complete && image.naturalWidth > 0);
  }

  updateButtons() {
    if (this.previousButton) {
      this.previousButton.disabled = this.currentIndex === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex === this.items.length - 1;
    }
  }
  
  handleTouchStart(e) {
    this.isDragging = true;
    this.startX = e.touches[0].clientX;
    this.startY = e.touches[0].clientY;
    this.translateX = -this.currentIndex * 100;
    this.isHorizontalSwipe = false;
    this.container.style.transition = 'none';
  }
  
  handleTouchMove(e) {
    if (!this.isDragging) return;
    
    this.currentX = e.touches[0].clientX;
    this.currentY = e.touches[0].clientY;
    
    const diffX = Math.abs(this.currentX - this.startX);
    const diffY = Math.abs(this.currentY - this.startY);
    
    // Determine if this is a horizontal swipe
    if (!this.isHorizontalSwipe && diffX > 10) {
      // If horizontal movement is greater than vertical, it's a horizontal swipe
      if (diffX > diffY) {
        this.isHorizontalSwipe = true;
      }
    }
    
    // Only prevent default and handle carousel if it's a horizontal swipe
    if (this.isHorizontalSwipe) {
      e.preventDefault();
      const containerWidth = this.container.offsetWidth;
      const percentage = ((this.currentX - this.startX) / containerWidth) * 100;
      const targetIndex = this.currentIndex + (percentage < 0 ? 1 : -1);

      this.preloadSlide(targetIndex);
      if (!this.isSlideReady(targetIndex)) return;
      
      const newTranslate = this.translateX + percentage;
      this.container.style.transform = `translateX(${newTranslate}%)`;
    }
  }
  
  handleTouchEnd(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    
    // Only handle carousel navigation if it was a horizontal swipe
    if (this.isHorizontalSwipe) {
      const containerWidth = this.container.offsetWidth;
      const diff = this.currentX - this.startX;
      const threshold = containerWidth * 0.25; // 25% of width to trigger slide
      let targetIndex = this.currentIndex;
      
      if (Math.abs(diff) > threshold) {
        if (diff > 0 && this.currentIndex > 0) {
          targetIndex = this.currentIndex - 1;
        } else if (diff < 0 && this.currentIndex < this.items.length - 1) {
          targetIndex = this.currentIndex + 1;
        }
      }
      
      if (targetIndex !== this.currentIndex) {
        this.goToSlide(targetIndex);
      } else {
        this.updatePosition();
      }
    }
    
    this.isHorizontalSwipe = false;
  }
  
  handleResize() {
    if (window.innerWidth >= 1025) {
      this.container.style.removeProperty('transition');
      this.container.style.removeProperty('transform');
      return;
    }
    
    // Update position on resize
    this.updatePosition(true);
  }
}

// Initialize when DOM is ready
function initMobileGalleryCarousel() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new MobileGalleryCarousel();
    });
  } else {
    new MobileGalleryCarousel();
  }
}

initMobileGalleryCarousel();

